import { useEffect, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { RefreshCw, MapPin, ShieldCheck, AlertTriangle, TrendingUp, Users } from "lucide-react";

// Stylised heat-map layout for the initial Zimbabwe deployment. This is one
// country configuration — the backend `/api/geo/intelligence` is country-agnostic
// and any African market plugs in the same way by supplying its own province set.
// so no hexagon body overlaps another and each label lives inside its own polygon.
// Labels are short + unique (no more `.split(" ")[0]` collision on Mashonaland N/E/W).
const ZW_PROVINCES = [
  { key: "Mashonaland West",    label: "MASH · WEST",    d: "M60,60 l110,0 l0,70 l-110,0 z",  cx: 115, cy: 95  },
  { key: "Mashonaland Central", label: "MASH · CENTRAL", d: "M170,60 l110,0 l0,70 l-110,0 z", cx: 225, cy: 95  },
  { key: "Mashonaland East",    label: "MASH · EAST",    d: "M280,60 l70,0 l0,70 l-70,0 z",   cx: 315, cy: 95  },
  { key: "Harare",              label: "HARARE",         d: "M240,130 l40,0 l0,25 l-40,0 z",  cx: 260, cy: 143 },
  { key: "Matabeleland North",  label: "MAT · NORTH",    d: "M60,130 l120,0 l0,75 l-120,0 z", cx: 120, cy: 168 },
  { key: "Midlands",            label: "MIDLANDS",       d: "M180,155 l100,0 l0,80 l-100,0 z",cx: 230, cy: 195 },
  { key: "Manicaland",          label: "MANICALAND",     d: "M280,130 l70,0 l0,120 l-70,0 z", cx: 315, cy: 190 },
  { key: "Matabeleland South",  label: "MAT · SOUTH",    d: "M60,205 l120,0 l0,80 l-120,0 z", cx: 120, cy: 245 },
  { key: "Bulawayo",            label: "BULAWAYO",       d: "M60,290 l60,0 l0,25 l-60,0 z",   cx: 90,  cy: 303 },
  { key: "Masvingo",            label: "MASVINGO",       d: "M180,235 l100,0 l0,80 l-100,0 z",cx: 230, cy: 275 },
];

// heatmap colour ramp — emerald green (low activity) → red (high)
const scale = (v, max) => {
  if (!max) return 0.05;
  return Math.min(1, Math.max(0.05, v / max));
};
const heatColour = (t) => {
  // 0 → soft grey; 1 → deep emerald; overlay red for fraud channel
  const g = Math.round(150 - 90 * t);
  const b = Math.round(120 - 90 * t);
  return `rgba(16, ${g}, ${b}, ${0.15 + 0.7 * t})`;
};
const fraudColour = (t) => `rgba(200, 30, 30, ${0.15 + 0.6 * t})`;

const METRICS = [
  { key: "verified",        label: "Verifications" },
  { key: "listings",        label: "Total listings" },
  { key: "certificates",    label: "Certificates issued" },
  { key: "trusted_updates", label: "Trusted updates" },
  { key: "escrows",         label: "Escrows" },
  { key: "fraud_index",     label: "Fraud index" },
];

export default function GeographicIntelligence() {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("verified");
  const [days, setDays] = useState(90);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/geo/intelligence", { params: { days } });
      setData(r.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  if (!data) return (<div><Navbar /><div className="max-w-[1400px] mx-auto px-6 py-10 mono text-sm">Loading geographic intelligence…</div></div>);

  const maxV = Math.max(1, ...data.provinces.map(p => p[metric] || 0));
  const isFraud = metric === "fraud_index";

  return (
    <div>
      <Navbar />
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="kicker">Executive · Geographic Intelligence</div>
            <h1 className="display text-4xl">African Trust Map</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Interactive heat map of verifications, fraud signals, escrow activity and Trust
              Network coverage across the current country configuration. Zimbabwe shown by default;
              additional African markets plug into the same view.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {[30, 90, 180, 365].map(d => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 text-xs mono uppercase tracking-widest border rounded-sm ${days === d ? "bg-primary text-primary-foreground border-primary" : "border-border"}`} data-testid={`geo-range-${d}`}>{d}d</button>
            ))}
            <Button variant="outline" size="sm" onClick={load} data-testid="geo-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4">
          {METRICS.map(m => (
            <button key={m.key} onClick={() => setMetric(m.key)}
              className={`px-3 py-1.5 text-xs mono uppercase tracking-widest border rounded-sm ${metric === m.key ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
              data-testid={`geo-metric-${m.key}`}>
              {m.label}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-6">
          {/* SVG map */}
          <div className="border border-border bg-card p-4 rounded-sm">
            <svg viewBox="0 0 420 340" className="w-full h-auto" role="img" aria-label="African Trust Map — verification heat map by province">
              {ZW_PROVINCES.map(p => {
                const row = data.provinces.find(r => r.province === p.key) || {};
                const v = row[metric] || 0;
                const t = scale(v, maxV);
                const fill = isFraud ? fraudColour(t) : heatColour(t);
                const isSelected = selected === p.key;
                return (
                  <g key={p.key} onClick={() => setSelected(p.key)} className="cursor-pointer" data-testid={`geo-hex-${p.key.replace(/\s+/g, '_')}`}>
                    <path d={p.d} fill={fill} stroke={isSelected ? "#0a6e4a" : "#8888"} strokeWidth={isSelected ? 2 : 0.8} />
                    <text x={p.cx} y={p.cy} textAnchor="middle" fontSize="8" fill="#111" className="mono uppercase tracking-widest pointer-events-none">{p.label}</text>
                    <text x={p.cx} y={p.cy + 12} textAnchor="middle" fontSize="11" fill="#111" fontWeight="700" className="pointer-events-none">{v}</text>
                  </g>
                );
              })}
            </svg>
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs text-muted-foreground">Low</span>
              <div className="h-2 flex-1 rounded-sm" style={{ background: isFraud ? "linear-gradient(90deg, rgba(200,30,30,0.15), rgba(200,30,30,0.85))" : "linear-gradient(90deg, rgba(16,150,120,0.15), rgba(16,60,30,0.85))" }} />
              <span className="text-xs text-muted-foreground">High</span>
            </div>
          </div>

          {/* Right rail */}
          <aside className="space-y-4">
            <div className="border border-border p-4 bg-card">
              <div className="kicker">National totals</div>
              <div className="mt-3 space-y-2 text-sm">
                <Row label="Total listings" value={data.totals.listings} Icon={TrendingUp} />
                <Row label="Verified" value={data.totals.verified} Icon={ShieldCheck} />
                <Row label="Certificates issued" value={data.totals.certificates} Icon={ShieldCheck} />
                <Row label="Escrows" value={data.totals.escrows} Icon={TrendingUp} />
                <Row label="Disputes" value={data.totals.disputes} Icon={AlertTriangle} />
                <Row label="Trusted updates" value={data.totals.trusted_updates} Icon={TrendingUp} />
                <Row label="Trust orgs" value={data.totals.organisations} Icon={Users} />
                <Row label="Active inspectors" value={data.inspectors_active} Icon={Users} />
              </div>
            </div>

            {selected ? <ProvincePanel row={data.provinces.find(r => r.province === selected)} onClear={() => setSelected(null)} /> : (
              <div className="border border-border p-4 bg-card mono text-xs text-muted-foreground">
                <MapPin className="h-4 w-4 mb-1 text-primary" />
                Tap a province on the map to drill in.
              </div>
            )}
          </aside>
        </div>

        {/* Ranked table */}
        <div className="mt-8 border border-border bg-card">
          <div className="p-4 border-b border-border kicker">Provinces ranked by {METRICS.find(m => m.key === metric)?.label}</div>
          <div className="divide-y divide-border">
            {[...data.provinces].sort((a,b) => (b[metric]||0) - (a[metric]||0)).map(r => (
              <div key={r.province} className="p-3 flex items-center gap-4 text-sm">
                <div className="w-40 font-semibold">{r.province}</div>
                <div className="flex-1 h-2 bg-secondary rounded-sm overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${((r[metric]||0)/maxV)*100}%` }} />
                </div>
                <div className="w-16 text-right mono">{r[metric] ?? 0}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ label, value, Icon }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-2 text-muted-foreground text-xs"><Icon className="h-3.5 w-3.5" /> {label}</div>
    <div className="mono">{value}</div>
  </div>
);

const ProvincePanel = ({ row, onClear }) => {
  if (!row) return null;
  return (
    <div className="border border-primary/40 p-4 bg-card">
      <div className="flex items-center justify-between">
        <div className="kicker">{row.province}</div>
        <button onClick={onClear} className="text-xs text-muted-foreground hover:text-primary">clear</button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <Metric label="Listings" v={row.listings} />
        <Metric label="Verified" v={row.verified} />
        <Metric label="Certificates" v={row.certificates} />
        <Metric label="Escrows" v={row.escrows} />
        <Metric label="Disputes" v={row.disputes} />
        <Metric label="Trust orgs" v={row.organisations} />
        <Metric label="Verify rate" v={`${row.verification_rate}%`} />
        <Metric label="Fraud index" v={`${row.fraud_index}%`} accent={row.fraud_index > 30 ? "text-red-600" : "text-emerald-700"} />
        <Metric label="Avg trust" v={row.avg_trust_score} />
        <Metric label="Trusted updates" v={row.trusted_updates} />
      </div>
    </div>
  );
};

const Metric = ({ label, v, accent }) => (
  <div className="border border-border p-2 rounded-sm">
    <div className="text-[10px] mono uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`display text-lg mt-0.5 ${accent || ""}`}>{v}</div>
  </div>
);
